"use client";

import { cn } from "@punchup/utils/cn";

const shimmer = "animate-pulse bg-neutral-200";

/**
 * Skeleton for the calendar content area.
 * Matches the output of VenuePageCalendarPage: heading + calendar grid/agenda.
 * The surrounding layout (header, nav, footer) remains rendered.
 *
 * Desktop: month navigation + 7-column calendar grid
 * Mobile: agenda view with month header + day rows
 */
export function CalendarPageSkeleton() {
  return (
    <div className="flex flex-col pb-10 lg:px-20 pt-4 md:pt-8">
      {/* "Calendar" heading */}
      <div className="px-4 pb-4 md:px-10 md:pb-6">
        <h2 className="text-[26px] md:text-[32px] font-bold text-primary-blue">
          Calendar
        </h2>
      </div>

      <div className="px-4 md:px-10">
        {/* Desktop: Month nav + grid */}
        <div className="hidden md:flex flex-col">
          {/* Month navigation */}
          <div className="flex items-center justify-between pb-3">
            <div className={cn("w-10 h-10 rounded-full", shimmer)} />
            <div className={cn("h-7 w-36 rounded", shimmer)} />
            <div className={cn("w-10 h-10 rounded-full", shimmer)} />
          </div>

          {/* Calendar grid */}
          <div className="border rounded-lg overflow-hidden">
            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 border-b bg-neutral-300">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="p-3 flex justify-center">
                  <div className="h-4 w-16 rounded animate-pulse bg-neutral-400/40" />
                </div>
              ))}
            </div>

            {/* Week rows */}
            {[1, 2, 3, 4, 5].map((week) => (
              <div key={week} className="grid grid-cols-7">
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <div
                    key={day}
                    className="flex flex-col items-start min-h-[100px] p-2 border-r border-b border-gray-200 last:border-r-0 bg-white"
                  >
                    <div className={cn("h-4 w-4 rounded mb-2", shimmer)} />
                    {(week + day) % 3 === 0 && (
                      <div className={cn("h-10 w-full rounded", shimmer)} />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Show count */}
          <div className={cn("mt-6 h-4 w-32 rounded", shimmer)} />
        </div>

        {/* Mobile: Agenda view */}
        <div className="flex flex-col md:hidden gap-y-6">
          <div className={cn("h-6 w-28 rounded", shimmer)} />
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex flex-row items-start py-4 border-b border-gray-200 last:border-b-0"
            >
              <div className="w-12 shrink-0">
                <div className={cn("h-3 w-8 rounded mb-1", shimmer)} />
                <div className={cn("h-7 w-6 rounded", shimmer)} />
              </div>
              <div className="flex-1 pl-4">
                <div className={cn("h-16 w-full rounded-lg", shimmer)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
